import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import myImg from "../../Assets/avatar.svg";
import Tilt from "react-parallax-tilt";
import { IconMail } from "@tabler/icons-react";
import {
  FaLinkedinIn,
  FaBullseye,
  FaCode,
  FaComments,
  FaGlobe,
  FaLayerGroup,
  FaPlug,
  FaProjectDiagram,
  FaRocket,
  FaSearch,
  FaShieldAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import {
  contactEmail,
  contactGmailComposeUrl,
  linkedinUrl,
} from "../../data/siteMeta";
import ProjectBriefForm from "./ProjectBriefForm";

function Home2() {
  const valuePoints = [
    {
      Icon: FaBullseye,
      title: "Clear direction",
      description: "Sharper structure and clearer next actions.",
    },
    {
      Icon: FaCode,
      title: "Full stack build",
      description: "Interface, API, and data layer handled together.",
    },
    {
      Icon: FaProjectDiagram,
      title: "Workflow-first UX",
      description: "Screens shaped around real product usage.",
    },
    {
      Icon: FaShieldAlt,
      title: "Dependable delivery",
      description: "Maintainable code and low-noise communication.",
    },
  ];

  const servicePackages = [
    {
      Icon: FaGlobe,
      label: "Website work",
      title: "Website build or redesign",
      description: "Sharper message, cleaner layout, stronger first impression.",
      signals: ["Messaging", "Responsive", "Conversion"],
      accent: "#60d5ff",
      accentSoft: "rgba(96, 213, 255, 0.12)",
      projectType: "Website build or redesign",
      ctaLabel: "Discuss website work",
    },
    {
      Icon: FaLayerGroup,
      label: "Product UI",
      title: "Portal, dashboard, or web app",
      description: "Calmer workflows, clearer states, faster comprehension.",
      signals: ["Portal flows", "Admin UX", "Dashboards"],
      accent: "#a78bfa",
      accentSoft: "rgba(167, 139, 250, 0.14)",
      projectType: "Web app, client portal, or dashboard",
      ctaLabel: "Discuss web app work",
    },
    {
      Icon: FaPlug,
      label: "Connected flow",
      title: "Website or app with integration",
      description: "Connected workflows that feel built in, not bolted on.",
      signals: ["APIs", "Handoffs", "Review states"],
      accent: "#34d399",
      accentSoft: "rgba(52, 211, 153, 0.14)",
      projectType: "Website or web app with supporting integration",
      ctaLabel: "Discuss integrated work",
    },
  ];

  const processSteps = [
    {
      Icon: FaSearch,
      title: "Clarify the problem",
      description: "Users, workflow, and the real friction point first.",
    },
    {
      Icon: FaComments,
      title: "Build in visible steps",
      description: "Steady slices, clear tradeoffs, no black-box progress.",
    },
    {
      Icon: FaRocket,
      title: "Ship something extendable",
      description: "Clean enough that the next iteration stays sane.",
    },
  ];

  const briefHighlights = [
    {
      label: "Best for",
      description: "Website refreshes, multi-step web apps, and integration-heavy flows.",
    },
    {
      label: "What you get",
      description: "A quick fit check and the clearest next step instead of vague back-and-forth.",
    },
    {
      label: "Reliable path",
      description: "Open a Gmail draft or copy the inquiry for any inbox you use.",
    },
  ];

  const engagementQuestions = [
    {
      question: "Can you work inside an existing codebase?",
      answer:
        "Yes. I can step into an existing codebase and improve the highest-value workflow first.",
    },
    {
      question: "How do projects usually start?",
      answer:
        "Usually with an audit, a scoped feature slice, or a plan for the riskiest workflow.",
    },
    {
      question: "Do you take on rushed work?",
      answer:
        "Only when scope is clear enough that speed will not damage quality.",
    },
  ];

  const startingOptions = [
    {
      label: "Audit",
      description: "Review the messy workflow before rebuilding it.",
    },
    {
      label: "Feature slice",
      description: "Own one high-value part first and prove the direction.",
    },
    {
      label: "Full build",
      description: "Take on the full build when the scope is already clear.",
    },
  ];

  return (
    <Container fluid className="home-about-section" id="about">
      <Container className="section-shell">
        <Row className="align-items-center gy-5 home-about-intro-row">
          <Col lg={7} className="home-about-description">
            <p className="section-kicker">What clients get</p>
            <h2 className="section-title">
              Product clarity across every layer.
            </h2>
            <p className="section-copy">
              Websites and web apps built end to end: interface, API, and data
              layer working as one product.
            </p>

            <div className="value-grid">
              {valuePoints.map((item) => (
                <article className="info-card info-card-compact" key={item.title}>
                  <span className="info-card-icon" aria-hidden="true">
                    <item.Icon />
                  </span>
                  <h3 className="info-card-title">{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </Col>
          <Col lg={5} className="myAvtar">
            <Tilt tiltMaxAngleX={6} tiltMaxAngleY={6} glareEnable={false}>
              <img
                src={myImg}
                className="img-fluid avatar-image"
                alt="Illustrated portrait of Om Jaiswal"
              />
            </Tilt>
          </Col>
        </Row>

        <Row className="section-block section-block-spacious card-grid-row">
          <Col md={12}>
            <div className="section-header section-header-left">
              <p className="section-kicker">Common engagements</p>
              <h2 className="section-title section-title-compact">
                Pick the right starting point.
              </h2>
              <p className="section-supporting-copy section-supporting-copy-left">
                Choose the closest fit and the brief starts with better context.
              </p>
            </div>
          </Col>

          {servicePackages.map((item) => (
            <Col md={6} lg={4} key={item.title}>
              <article
                className="service-card service-card-featured"
                style={{
                  "--service-accent": item.accent,
                  "--service-accent-soft": item.accentSoft,
                }}
              >
                <div className="service-card-top">
                  <span className="service-card-kicker">{item.label}</span>
                  <div className="service-card-icon" aria-hidden="true">
                    <item.Icon />
                  </div>
                </div>
                <h3 className="service-card-title">{item.title}</h3>
                <p className="service-card-summary">{item.description}</p>
                <div className="service-card-signal-row" aria-label={`${item.title} key strengths`}>
                  {item.signals.map((signal) => (
                    <span key={signal} className="service-card-signal">
                      {signal}
                    </span>
                  ))}
                </div>
                <Button
                  as={Link}
                  to={{
                    pathname: "/",
                    search: `?projectType=${encodeURIComponent(item.projectType)}`,
                    hash: "#project-brief",
                  }}
                  variant="outline-light"
                  className="service-card-cta"
                >
                  {item.ctaLabel}
                </Button>
              </article>
            </Col>
          ))}
        </Row>

        <Row className="section-block section-block-tight gy-4">
          <Col md={12}>
            <div className="section-header section-header-left">
              <p className="section-kicker">Working approach</p>
              <h2 className="section-title section-title-compact">
                Three steps, visible all the way.
              </h2>
            </div>

            <div className="process-grid process-grid-wide">
              {processSteps.map((step, index) => (
                <article className="process-card" key={step.title}>
                  <div className="process-card-top">
                    <span className="process-card-icon" aria-hidden="true">
                      <step.Icon />
                    </span>
                    <span className="process-step-number">0{index + 1}</span>
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
          </Col>
        </Row>

        <Row className="section-block section-block-tight gy-4">
          <Col md={12}>
            <aside
              className="inquiry-card inquiry-card-form inquiry-card-form-wide"
              id="project-brief"
              tabIndex="-1"
            >
              <p className="section-kicker">Project brief</p>
              <h3>Send a short inquiry</h3>
              <p>Two quick inputs. No long brief needed.</p>
              <ProjectBriefForm />

              <div className="brief-highlight-grid">
                {briefHighlights.map((item) => (
                  <article key={item.label} className="brief-highlight-card">
                    <span className="project-mini-heading">{item.label}</span>
                    <p>{item.description}</p>
                  </article>
                ))}
              </div>
            </aside>
          </Col>
        </Row>

        <Row className="section-block section-block-tight align-items-start gy-4">
          <Col lg={7}>
            <div className="section-header section-header-left">
              <p className="section-kicker">Engagement FAQ</p>
              <h2 className="section-title section-title-compact">
                Quick answers before you reach out.
              </h2>
            </div>

            <div className="faq-grid">
              {engagementQuestions.map((item, index) => (
                <details
                  key={item.question}
                  className="faq-item"
                  open={index === 0}
                >
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </Col>

          <Col lg={5}>
            <article className="engagement-note-card">
              <p className="section-kicker">Good starting points</p>
              <h3>Start with the smallest useful move.</h3>
              <div className="starting-options-grid">
                {startingOptions.map((item) => (
                  <article key={item.label} className="starting-option-card">
                    <span className="project-mini-heading">{item.label}</span>
                    <p>{item.description}</p>
                  </article>
                ))}
              </div>
            </article>
          </Col>
        </Row>

        <Row>
          <Col md={12} className="home-about-social">
            <div className="contact-strip">
              <div className="contact-strip-copy">
                <h2>Need the clearest next step?</h2>
                <p>
                  Send a short brief or email directly at
                  <strong> {contactEmail}</strong>.
                </p>
              </div>
              <div className="contact-strip-actions">
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-link"
                  aria-label="Open Om Jaiswal LinkedIn profile in a new tab"
                >
                  <FaLinkedinIn />
                  <span>LinkedIn</span>
                </a>
                <a
                  href={contactGmailComposeUrl}
                  className="contact-link"
                  aria-label="Open Gmail to email Om Jaiswal"
                >
                  <IconMail />
                  <span>Email</span>
                </a>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default Home2;
